import { AppRole, CustomRoute } from "@/interfaces";
import { getAdminRoutes } from "./admin";
import clientRoutes from "./client";
import employeeRoutes from "./employee";
import guestRoutes from "./guest";
import superAdminRoutes from "./superAdmin";
import financeRoutes from "./finance";
import vendorRoutes from "./vendor";

/**
 * Get routes based on user role
 * @param role - User role
 * @returns Array of routes for the specified role
 */
export const getCurrentUserRouter = (role: AppRole, user?: any): CustomRoute[] => {
  // Normalize role to lowercase for comparison
  const normalizedRole = role;

  switch (normalizedRole) {
    case "Super":
      return superAdminRoutes;
    case "Admin":
      return getAdminRoutes();
    case "Employee":
      return employeeRoutes;
    case "User": {
      // If user is ONLY a vendor (has vendorId but NO clientId/clientTeamMemberships), show restricted vendor routes
      // Note: We check for client team memberships or direct clientId association to determine if they are a client user
      const isClient = !!user?.clientId || (user?.clientTeamMemberships && user.clientTeamMemberships.length > 0);
      const isVendor = !!user?.vendorId;

      if (isVendor && !isClient) {
        // Vendor Only - Custom mix of Vendor dashboard + Bill features
        const vendorDashboard = clientRoutes.find((r) => r.menuName === "Dashboard");
        const vendorProfile = vendorRoutes.find((r) => r.menuName === "Profile");
        // Reuse client routes for bill flows only
        const myBills = clientRoutes.find((r) => r.menuName === "All Bills");
        const createBill = clientRoutes.find((r) => r.menuName === "Create Bill");
        const notifications = clientRoutes.find((r) => r.menuName === "Notifications");

        const mixedRoutes: CustomRoute[] = [];
        if (vendorDashboard) mixedRoutes.push(vendorDashboard);
        if (createBill) mixedRoutes.push(createBill);
        if (myBills) mixedRoutes.push({ ...myBills, menuName: "My Bills" }); // Rename if needed
        if (notifications) mixedRoutes.push(notifications);
        if (vendorProfile) mixedRoutes.push(vendorProfile);

        return mixedRoutes;
      }
      // If Client OR Both (Client + Vendor) -> Show full Client suite,
      // but filter out vendor-specific routes if not a vendor
      if (isClient && !isVendor) {
        return clientRoutes.filter((r) => r.menuName !== "All Bills" && r.menuName !== "Create Bill");
      }
      return clientRoutes;
    }
    case "Finance":
      return financeRoutes;
    default:
      return guestRoutes;
  }
};
